import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  formatAdminTableDate,
  type AdminTableDateOptions,
} from "../../../lib/formatAdminTableDate";
import { AdminDeleteForm } from "./AdminDeleteForm";
import type { AdminChatsPayload, AdminRouteMap } from "../types";

type AdminChatsSectionProps = {
  chats: AdminChatsPayload;
  dateOptions: AdminTableDateOptions;
  csrfToken: string;
  adminRoutes: AdminRouteMap;
  /** When true, hide user/business columns (single-account detail view). */
  accountScoped?: boolean;
};

function shortConversationId(id: string): string {
  if (id.length <= 16) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

export const AdminChatsSection: React.FC<AdminChatsSectionProps> = ({
  chats,
  dateOptions,
  csrfToken,
  adminRoutes,
  accountScoped = false,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggle = (sessionId: string) => {
    setExpandedId((cur) => (cur === sessionId ? null : sessionId));
  };

  const snippetColSpan = accountScoped ? 5 : 7;

  return (
    <>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Chats</CardTitle>
        </CardHeader>
        <CardContent>
          {chats.conversations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No conversations with a session id for this account.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Session</th>
                    <th className="text-left py-3 px-4 font-medium">Stage</th>
                    {!accountScoped && (
                      <>
                        <th className="text-left py-3 px-4 font-medium">
                          User
                        </th>
                        <th className="text-left py-3 px-4 font-medium">
                          Business
                        </th>
                      </>
                    )}
                    <th className="text-left py-3 px-4 font-medium">
                      Messages
                    </th>
                    <th className="text-left py-3 px-4 font-medium">
                      Last activity
                    </th>
                    <th className="text-left py-3 px-4 font-medium min-w-44">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {chats.conversations.map((c) => (
                    <React.Fragment key={c.conversationSessionId}>
                      <tr className="border-b border-border last:border-0">
                        <td
                          className="py-3 px-4 font-mono text-xs"
                          title={c.conversationSessionId}
                        >
                          {shortConversationId(c.conversationSessionId)}
                        </td>
                        <td className="py-3 px-4">{c.context}</td>
                        {!accountScoped && (
                          <>
                            <td className="py-3 px-4">
                              {c.userEmail ?? `user #${c.userId}`}
                            </td>
                            <td className="py-3 px-4 text-muted-foreground">
                              {c.businessSubdomain ?? "—"}
                            </td>
                          </>
                        )}
                        <td className="py-3 px-4">{c.messageCount}</td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {formatAdminTableDate(c.lastMessageAt, dateOptions)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => toggle(c.conversationSessionId)}
                            >
                              {expandedId === c.conversationSessionId
                                ? "Hide"
                                : "Snippet"}
                            </Button>
                            <AdminDeleteForm
                              action={adminRoutes.adminDestroyChatSession.replace(
                                ":conversation_session_id",
                                encodeURIComponent(c.conversationSessionId),
                              )}
                              csrfToken={csrfToken}
                              confirmMessage="Delete this entire conversation from the audit log?"
                            />
                          </div>
                        </td>
                      </tr>
                      {expandedId === c.conversationSessionId && (
                        <tr className="border-b border-border bg-muted/30">
                          <td colSpan={snippetColSpan} className="py-4 px-4">
                            <p className="text-xs text-muted-foreground mb-3">
                              Preview: {c.preview}
                            </p>
                            <ul className="space-y-3 text-sm">
                              {c.messages.map((m, idx) => (
                                <li
                                  key={`${c.conversationSessionId}-${m.createdAt}-${idx}`}
                                  className="border-l-2 border-premium-200 pl-3"
                                >
                                  <span className="font-medium text-foreground">
                                    {m.role}
                                  </span>
                                  <span className="text-muted-foreground text-xs ml-2">
                                    {formatAdminTableDate(
                                      m.createdAt,
                                      dateOptions,
                                    )}
                                  </span>
                                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                                    {m.body}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <details className="rounded-xl border border-border bg-card">
        <summary className="cursor-pointer list-none px-4 py-3 font-medium text-sm flex items-center justify-between gap-2">
          <span>Uncategorized messages</span>
          <span className="text-muted-foreground font-normal">
            {chats.uncategorized.length} shown
          </span>
        </summary>
        <div className="px-4 pb-4">
          {chats.uncategorized.length === 0 ? (
            <p className="text-sm text-muted-foreground pt-1">
              No uncategorized messages for this account.
            </p>
          ) : (
            <div className="overflow-x-auto pt-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 font-medium">When</th>
                    <th className="text-left py-2 px-3 font-medium">Stage</th>
                    <th className="text-left py-2 px-3 font-medium">Role</th>
                    {!accountScoped && (
                      <th className="text-left py-2 px-3 font-medium">User</th>
                    )}
                    <th className="text-left py-2 px-3 font-medium">Body</th>
                    <th className="text-left py-2 px-3 font-medium w-28">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {chats.uncategorized.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-border last:border-0 align-top"
                    >
                      <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">
                        {formatAdminTableDate(row.createdAt, dateOptions)}
                      </td>
                      <td className="py-2 px-3">{row.context}</td>
                      <td className="py-2 px-3">{row.role}</td>
                      {!accountScoped && (
                        <td className="py-2 px-3">
                          {row.userEmail ?? `user #${row.userId}`}
                        </td>
                      )}
                      <td className="py-2 px-3 whitespace-pre-wrap text-muted-foreground max-w-md">
                        {row.body}
                      </td>
                      <td className="py-2 px-3">
                        <AdminDeleteForm
                          action={adminRoutes.adminDestroyChatMessage.replace(
                            ":id",
                            String(row.id),
                          )}
                          csrfToken={csrfToken}
                          confirmMessage="Delete this uncategorized message from the audit log?"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </details>
    </>
  );
};
